import type {
  AgentMode,
  ConversationEventDetails,
  EventType,
  HookType,
  Timestamp,
  UUID,
} from '@/index'
import type { EncryptionStatus } from '@/features/events/shared/types/EncryptionInfo'
import type { FileChanges, NotificationContext } from '@/infra/sse/conversation/payloads'

export interface ConversationTurnEntry {
  readonly eventId: UUID
  readonly timestamp: Timestamp
  readonly hookType: HookType | null
  readonly eventType: EventType
  readonly notificationMessage: string | null
  readonly toolName: string | null
  readonly notificationContext: NotificationContext | null
  readonly agentMode: AgentMode
  readonly agentResponse: string | null
  readonly fileChanges: FileChanges | null
  readonly userPrompt: string | null
  readonly encryption: EncryptionStatus
}

export function conversationTurnEntryFrom(
  conversationEvent: ConversationEventDetails,
): ConversationTurnEntry {
  return {
    eventId: conversationEvent.eventMetadata.eventId,
    timestamp: conversationEvent.eventMetadata.timestamp,
    hookType: conversationEvent.eventMetadata.hookType,
    eventType: conversationEvent.eventType,
    notificationMessage: conversationEvent.notificationMessage,
    toolName: conversationEvent.toolName,
    notificationContext: normalizeNotificationContext(conversationEvent.notificationContext),
    agentMode: conversationEvent.agentMode,
    agentResponse: conversationEvent.agentResponse
      ? stripTrailingColon(conversationEvent.agentResponse)
      : null,
    fileChanges: conversationEvent.fileChanges,
    userPrompt: conversationEvent.userPrompt,
    encryption: conversationEvent.encryption,
  }
}

function normalizeNotificationContext(
  context: NotificationContext | null,
): NotificationContext | null {
  if (!context) return null
  if (!context.agentDialogue?.userOptions?.length) return context

  return {
    ...context,
    agentDialogue: {
      ...context.agentDialogue,
      userOptions: context.agentDialogue.userOptions.map((option) => ({
        ...option,
        option: stripParenthetical(option.option),
      })),
    },
  }
}

function stripTrailingColon(text: string): string {
  return text.replace(/:$/, '').trim()
}

function stripParenthetical(text: string): string {
  return text.replace(/\s*\([^)]*\)\s*$/, '').trim()
}
